import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { getProducts, verifySubscription } from '../../Services/subscription';
import { sendAppLink } from '../../Services/auth';
import { getUserProfile } from '../../Services/MyProfile';
import { findPlanProduct } from '../../Utils/planUtils';
import { useHeader } from '../../Providers/HeaderContextProvider';
import arisLogo from '../../Assets/icon/aris_logo.svg';
import uatQRCode from '../../Assets/icon/uat-aris-app-code.png';
import loadingVideo from '../../Assets/Video/loadingSphere.webm';
import './AuthPages.css';

const PaymentSuccessQRPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setHeaderContent, addProfileData } = useHeader();
  const [planInfo, setPlanInfo] = useState(null);
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [sentPhoneNumber, setSentPhoneNumber] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationError, setVerificationError] = useState('');
  
  const planName = searchParams.get('plan');
  const subscriptionMode = searchParams.get('subscription_mode');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    setHeaderContent(false);
    verifyCheckout();
  }, []);

  const verifyCheckout = async () => {
    try {
      setIsVerifying(true);
      setVerificationError('');

      // Get session_id from URL
      if (!sessionId) {
        throw new Error('No session ID found');
      }

      // Verify the checkout with backend
      const profile = JSON.parse(localStorage.getItem('profile') || '{}');

      const verifyPayload = {
        checkout_session_id: sessionId,
      };

      const verifyResponse = await verifySubscription(verifyPayload);

      if (verifyResponse.status === 'succeeded') {
        // Refresh profile data to get updated subscription info
        if (addProfileData) {
          await addProfileData();
        }

        // Fetch fresh profile from API
        try {
          const freshProfile = await getUserProfile();
          if (freshProfile) {
            localStorage.setItem('profile', JSON.stringify(freshProfile));
          }
        } catch (err) {
          console.error('Error fetching profile:', err);
        }

        // Fetch plan info for display
        if (planName && subscriptionMode) {
          await fetchPlanInfo();
        }

        setIsVerifying(false);
      } else {
        throw new Error(verifyResponse.message || 'Payment verification failed');
      }
    } catch (err) {
      console.error('Checkout verification error:', err);
      setVerificationError(err.message || 'Failed to verify payment');
      setIsVerifying(false);
      
      // Redirect to error page or login after 3 seconds
      setTimeout(() => {
        navigate('/login?payment=failed');
      }, 3000);
    }
  };

  // Resend timer
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const fetchPlanInfo = async () => {
    try {
      const response = await getProducts();
      if (response?.payload?.products) {
        const planData = findPlanProduct(response.payload.products, planName, subscriptionMode);
        
        if (planData) {
          setPlanInfo({
            name: planData.displayName,
            price: planData.price,
            interval: planData.interval
          });
        }
      }
    } catch (err) {
      console.error('Error fetching plan info:', err);
    }
  };

  const getQRCodeImage = () => {
    // Check if we're in production environment
    const isProduction = process.env.REACT_APP_ENV === 'production' || 
                        process.env.NODE_ENV === 'production';
    
    // For production, use environment variable or fallback to API-generated QR
    if (isProduction && process.env.REACT_APP_PROD_QR_CODE) {
      return process.env.REACT_APP_PROD_QR_CODE;
    }
    
    // For UAT/development, use local QR code image
    return uatQRCode;
  };

  const handleSendLinkClick = () => {
    setShowPhoneInput(true);
  };

  const validatePhone = (phoneValue) => {
    try {
      const parsed = parsePhoneNumberFromString(phoneValue);
      if (!parsed) {
        return { valid: false, error: 'Please enter a valid phone number' };
      }
      if (!parsed.isValid()) {
        return { valid: false, error: 'Please enter a valid phone number' };
      }
      return { 
        valid: true, 
        contact: parsed.nationalNumber,
        countryCode: `+${parsed.countryCallingCode}`
      };
    } catch (err) {
      return { valid: false, error: 'Please enter a valid phone number' };
    }
  };

  const handleSendLink = async () => {
    setPhoneError('');
    
    const validation = validatePhone(phone);
    if (!validation.valid) {
      setPhoneError(validation.error);
      return;
    }

    setIsSending(true);
    try {
      const response = await sendAppLink(phone);

      if (response.status === 200) {
        setLinkSent(true);
        setSentPhoneNumber(phone);
        setResendTimer(59);
      } else {
        setPhoneError(response.message || 'Failed to send link. Please try again.');
      }
    } catch (err) {
      setPhoneError(err.message || 'Failed to send link. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleResendLink = async () => {
    if (resendTimer > 0) return;
    
    setPhoneError('');
    setIsSending(true);
    
    try {
      const response = await sendAppLink(sentPhoneNumber);

      if (response.status === 200) {
        setResendTimer(59);
      } else {
        setPhoneError(response.message || 'Failed to resend link. Please try again.');
      }
    } catch (err) {
      setPhoneError(err.message || 'Failed to resend link. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  // Show loading state while verifying
  if (isVerifying) {
    return (
      <div className="auth-page-container">
        <div className="auth-page-content qr-page-content">
          <img src={arisLogo} alt="Aris" className="auth-page-logo" />
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <video 
              src={loadingVideo} 
              autoPlay 
              loop 
              muted 
              playsInline
              style={{ width: '120px', height: '120px', marginBottom: '24px' }}
            />
            <h2 className="payment-success-title">Verifying your payment...</h2>
            <p className="payment-success-subtitle">Please wait while we confirm your subscription.</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if verification failed
  if (verificationError) {
    return (
      <div className="auth-page-container">
        <div className="auth-page-content qr-page-content">
          <img src={arisLogo} alt="Aris" className="auth-page-logo" />
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <h2 className="payment-success-title" style={{ color: '#E53E3E' }}>Payment Verification Failed</h2>
            <p className="payment-success-subtitle">{verificationError}</p>
            <p className="payment-success-subtitle">Redirecting you back to login...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page-container">
      <div className="auth-page-content qr-page-content">
        <img src={arisLogo} alt="Aris" className="auth-page-logo" />
        
        <h2 className="payment-success-title">Payment successful. Ready to start?</h2>
        <p className="payment-success-subtitle">Your receipt has been emailed.</p>

        <div className="qr-code-container">
          <img 
            src={getQRCodeImage()} 
            alt="QR Code" 
            className="qr-code-image"
          />
        </div>

        <p className="qr-scan-instruction">
          Scan with your phone to download Aris instantly
        </p>

        {!linkSent && (
          <>
            <div className="payment-qr-divider">
              <span>or</span>
            </div>

            {!showPhoneInput ? (
              <button
                type="button"
                className="send-link-btn"
                onClick={handleSendLinkClick}
              >
                Send a link to my phone
              </button>
            ) : (
              <div className="phone-input-section">
                <p className="phone-input-instruction">
                  Add your mobile number and we'll send you a link to download the app
                </p>
                
                <div className="phone-input-wrapper">
                  <PhoneInput
                    defaultCountry="US"
                    value={phone}
                    onChange={(value) => {
                      setPhone(value);
                      setPhoneError('');
                    }}
                    className="payment-phone-input"
                  />
                </div>

                {phoneError && (
                  <p className="phone-error-message">{phoneError}</p>
                )}

                <button
                  type="button"
                  className="send-link-submit-btn"
                  onClick={handleSendLink}
                  disabled={isSending || !phone}
                >
                  {isSending ? 'Sending...' : 'Send Link'}
                </button>
              </div>
            )}
          </>
        )}

        {linkSent && (
          <div className="link-sent-section">
            <p className="link-sent-phone">{sentPhoneNumber}</p>
            <p className="link-sent-message">
              Link sent. Check your phone to continue.<br />
              You can close this page once you've opened the app.
            </p>
            
            {resendTimer > 0 ? (
              <p className="resend-timer">Resend the link in {resendTimer}s</p>
            ) : (
              <button
                type="button"
                className="resend-link-btn"
                onClick={handleResendLink}
                disabled={isSending}
              >
                Resend the link
              </button>
            )}
          </div>
        )}

        <p className="auth-page-footer">© 2026 Aristotle, Inc. All rights reserved. Patents pending.</p>
      </div>
    </div>
  );
};

export default PaymentSuccessQRPage;
